/* ============================================================
   Toee Tire — the header vehicle chip and its picker.
   Every [data-vehicle-chip] button shows the Current vehicle (or "Shop by
   vehicle"); clicking one opens the single shared picker (snippets/toee-vehicle-chip.liquid):
   Year → Make → Model → Trim from /apps/pro/vehicle/options, then the Fitment
   buttons from /apps/pro/vehicle/fitment. Save writes the Current vehicle.

   ToeeVehicleChip.openPicker(anchorEl) opens it from anywhere (collection banner,
   product page, cart "Change"). chipView is pure, for tests.

   It also keeps the cart's Vehicle / Vehicle ID attributes in step with the Current vehicle
   (syncCart) and paints the cart page's "This order is for ..." line (paintCartLine).
   ============================================================ */
(function (root, factory) {
  var api = factory(root);
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }
  if (root && typeof root === 'object') {
    root.ToeeVehicleChip = api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function (root) {
  'use strict';

  var current = typeof module === 'object' && module.exports ? require('./toee-vehicle-current.js') : root && root.ToeeVehicleCurrent;
  var LEVELS = ['year', 'make', 'model', 'trim'];
  var BASE = '/apps/pro';

  function fill(tpl, vars) {
    return String(tpl || '').replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, k) { return vars && vars[k] != null ? vars[k] : ''; });
  }

  /** What a chip shows: {set, text (full name), short (CX-5 ’16), aria}. */
  function chipView(vehicle, i18n) {
    var t = i18n || {};
    if (!vehicle) return { set: false, text: t.chip_empty || '', short: t.chip_empty || '', aria: t.chip_empty || '' };
    return {
      set: true,
      text: vehicle.label,
      short: current.shortLabel(vehicle),
      aria: fill(t.chip_aria, { label: vehicle.label }),
    };
  }

  function allFour(selected) {
    return LEVELS.every(function (l) { return !!(selected && selected[l]); });
  }

  function carFields(o) {
    return { year: o.year, make: o.make, model: o.model, trim: o.trim, makeName: o.makeName, modelName: o.modelName };
  }

  /**
   * What Save writes, or null when Save must stay disabled. `selected` = the four selects' values
   * plus makeName / modelName; `stored` = the Current vehicle the picker opened with (or null);
   * `changed` = the shopper has touched a level since opening. Untouched and not all loaded (offline,
   * a stored value gone from the options): the stored car's own fields go with the chosen Fitment, never blanks.
   */
  function saveInput(selected, stored, fitmentId, changed) {
    if (!fitmentId) return null;
    if (allFour(selected)) return carFields(selected);
    if (!changed && stored) return carFields(stored);
    return null;
  }

  /**
   * The next level to load when opening on the stored car: the first level whose select does not yet
   * hold the stored value, with that value to keep; null when all four are in place (or no stored car).
   * Retry after a failed load calls this again, so it resumes at the failed level.
   */
  function prefillPlan(stored, selected) {
    if (!stored) return null;
    for (var i = 0; i < LEVELS.length; i++) {
      var l = LEVELS[i];
      if (!selected || selected[l] !== stored[l]) return { level: l, keep: stored[l] };
    }
    return null;
  }

  /**
   * Shopify's `t` filter HTML-escapes its output ("Couldn&#39;t"), and `| json` keeps that, so the
   * strings reach the script escaped. They go in through textContent, so decode them once on read.
   */
  function decodeEntities(s) {
    return String(s == null ? '' : s).replace(/&(#\d+|#x[0-9a-f]+|amp|lt|gt|quot|apos);/gi, function (m, e) {
      var named = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" };
      if (e.charAt(0) !== '#') return named[e.toLowerCase()];
      var code = e.charAt(1).toLowerCase() === 'x' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
      return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : m;
    });
  }

  function readI18n(text) {
    var raw = JSON.parse(text || '{}');
    var out = {};
    Object.keys(raw).forEach(function (k) { out[k] = typeof raw[k] === 'string' ? decodeEntities(raw[k]) : raw[k]; });
    return out;
  }

  /**
   * Is a click outside the picker and the chip that opened it? A target that has left the document
   * (a fitment button the picker just re-rendered) was inside when it was clicked, so it is not outside.
   */
  function isOutsideClick(picker, anchor, target, doc) {
    if (!target) return true;
    if (doc && !doc.contains(target)) return false;
    if (picker && picker.contains(target)) return false;
    if (anchor && anchor.contains && anchor.contains(target)) return false;
    return true;
  }

  var api = {
    chipView: chipView, saveInput: saveInput, prefillPlan: prefillPlan,
    decodeEntities: decodeEntities, readI18n: readI18n, isOutsideClick: isOutsideClick,
    openPicker: function () {},
    closePicker: function () {},
    paintCartLine: function () {},
  };
  if (typeof document === 'undefined' || !current || !current.store) return api;

  var store = current.store;
  var doc = document;
  var picker = doc.querySelector('[data-vehicle-picker]');
  var i18n = {};
  try { i18n = readI18n((doc.getElementById('toee-vehicle-i18n') || {}).textContent); } catch (e) { i18n = {}; }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function getJson(url) {
    return fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (body) {
        return { ok: res.ok, status: res.status, body: body || {} };
      });
    });
  }

  /* ---------- chips ---------- */

  function renderChips() {
    var view = chipView(store.read(), i18n);
    var chips = doc.querySelectorAll('[data-vehicle-chip]');
    Array.prototype.forEach.call(chips, function (chip) {
      var text = chip.querySelector('.toee-vehicle__text');
      // The desktop chip has room for the full name; the mobile one (tablet) shows the short name.
      if (text) text.textContent = chip.getAttribute('data-vehicle-chip') === 'desktop' ? view.text : view.short;
      chip.classList.toggle('is-set', view.set);
      chip.setAttribute('aria-label', view.aria);
    });
    return chips;
  }

  /** The chip on screen at this width (desktop or tablet header), else the first one, else null. */
  function visibleChip() {
    var chips = doc.querySelectorAll('[data-vehicle-chip]');
    for (var i = 0; i < chips.length; i++) if (chips[i].getClientRects().length) return chips[i];
    return chips[0] || null;
  }

  function flash(chips) {
    Array.prototype.forEach.call(chips, function (chip) {
      chip.classList.add('is-flash');
      setTimeout(function () { chip.classList.remove('is-flash'); }, 150);
    });
  }

  /* ---------- cart: the order carries the Current vehicle, and the cart page says so ---------- */

  var cartEl = doc.querySelector('[data-vehicle-cart]'); // the header's copy of cart.attributes
  var cartBusy = false; // one /cart/update.js at a time
  var cartAgain = false; // the store changed while it was running

  /**
   * Mirror the store into the cart attributes when they differ. Any failure (a 4xx, a guest the store
   * lockdown rejects, offline) is silent and is not retried until the store changes again.
   */
  function syncCart() {
    if (!cartEl) return;
    if (cartBusy) { cartAgain = true; return; }
    var attrs = current.cartSync({
      vehicle: cartEl.getAttribute('data-vehicle'),
      vehicleId: cartEl.getAttribute('data-vehicle-id'),
    }, store.read());
    if (!attrs) return;
    cartBusy = true;
    cartAgain = false;
    Promise.resolve().then(function () {
      return fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        keepalive: true, // survives Checkout or Shop again leaving the page right after a change
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: attrs }),
      });
    }).then(function (res) {
      if (!res.ok) throw new Error('cart');
      cartEl.setAttribute('data-vehicle', attrs.Vehicle);
      cartEl.setAttribute('data-vehicle-id', attrs['Vehicle ID']);
    }).catch(function () { /* silent: the next store change tries again */ }).then(function () {
      cartBusy = false;
      if (cartAgain) syncCart();
    });
  }

  var cartLineI18n = null;

  /** "This order is for <b>label · size</b>", from the store; hidden when there is no vehicle. Text nodes only. */
  function paintCartLine() {
    var line = doc.querySelector('[data-vehicle-cart-line]');
    if (!line) return;
    var v = store.read();
    var f = current.currentFitment(v);
    line.hidden = !f;
    if (!f) return;
    if (!cartLineI18n) {
      try { cartLineI18n = readI18n(line.querySelector('[data-vehicle-cart-line-i18n]').textContent); } catch (e) { cartLineI18n = {}; }
    }
    var tpl = cartLineI18n.cart_for || '{{ label }}';
    var m = /\{\{\s*label\s*\}\}/.exec(tpl);
    var msg = line.querySelector('[data-vehicle-cart-for]');
    msg.textContent = '';
    var before = m ? tpl.slice(0, m.index) : tpl;
    var after = m ? tpl.slice(m.index + m[0].length) : '';
    if (before) msg.appendChild(doc.createTextNode(before));
    if (m) {
      var b = doc.createElement('strong');
      b.textContent = current.cartAttributes(v).Vehicle;
      msg.appendChild(b);
    }
    if (after) msg.appendChild(doc.createTextNode(after));
  }

  api.paintCartLine = paintCartLine; // script.cart.js repaints after CartReload swaps .bmw-cart-summary

  if (doc.querySelector('[data-vehicle-cart-line]')) {
    // Delegated on the document, so it keeps working on the line CartReload puts in after a quantity change.
    // Registered before the picker's own document click handler, so Change has opened the picker (anchored on the
    // line, which contains the button) by the time that handler asks whether the click was outside.
    doc.addEventListener('click', function (e) {
      var t = e.target;
      var line = t && t.closest && t.closest('[data-vehicle-cart-line]');
      if (!line) return;
      if (t.closest('[data-vehicle-cart-clear]')) {
        api.closePicker(); // an open picker is anchored on this line, which is about to hide
        store.clear();
        // The line (and the focused button in it) is hidden now: focus goes to the header chip on screen.
        var chip = visibleChip();
        if (chip && chip.getClientRects().length) chip.focus();
      } else if (t.closest('[data-vehicle-cart-change]')) api.openPicker(line);
    });
  }

  syncCart();
  paintCartLine();
  store.onChange(function () { syncCart(); paintCartLine(); });

  // Back / Forward restored this page from the cache: the store (or the cart, after a checkout) may have moved on.
  // The header's copy of the cart attributes is unknown now, so it is blanked and the next sync writes the store.
  root.addEventListener('pageshow', function (e) {
    if (!e || !e.persisted) return;
    renderChips();
    paintCartLine();
    if (cartEl) {
      cartEl.setAttribute('data-vehicle', '');
      cartEl.setAttribute('data-vehicle-id', '');
    }
    syncCart();
  });

  /* ---------- Account: "Shop this vehicle again" ([data-vehicle-shop], delegated) ---------- */

  var SHOP_AGAIN_TIMEOUT_MS = 8000;

  /** The one status line after the button; a retry reuses it (and text only, never markup). */
  function shopAgainStatus(btn, text) {
    var next = btn.nextElementSibling;
    var line = next && next.hasAttribute && next.hasAttribute('data-vehicle-shop-status') ? next : null;
    if (!line && text && btn.parentNode) {
      line = doc.createElement('span');
      line.setAttribute('role', 'status');
      line.setAttribute('data-vehicle-shop-status', '');
      line.className = 'bmw-ca-split__vehicle-error';
      btn.parentNode.insertBefore(line, btn.nextSibling);
    }
    if (line) line.textContent = text || '';
  }

  /** getJson that gives up after SHOP_AGAIN_TIMEOUT_MS, so a hung request cannot leave the button disabled. */
  function shopAgainFetch(url) {
    return new Promise(function (resolve, reject) {
      var timer = setTimeout(function () { reject(new Error('timeout')); }, SHOP_AGAIN_TIMEOUT_MS);
      getJson(url).then(function (r) { clearTimeout(timer); resolve(r); }, function (err) { clearTimeout(timer); reject(err); });
    });
  }

  function shopAgain(btn) {
    var idText = btn.getAttribute('data-vehicle-shop');
    var plan = current.shopAgainQuery(idText);
    var failText = btn.getAttribute('data-vehicle-shop-error') || '';
    shopAgainStatus(btn, '');
    if (!plan) { shopAgainStatus(btn, failText); return; }
    btn.disabled = true;
    var parts = current.parseVehicleId(idText); // year / make / model / trim as picked; names fall back to slugs
    shopAgainFetch(BASE + '/vehicle/fitment?' + plan.query).then(function (r) {
      if (!r.ok) throw new Error('fitment');
      var fitmentId = current.pickFitmentId(r.body.fitments, plan.fitmentId);
      if (!fitmentId) throw new Error('no fitment');
      var record = current.fromFitmentResponse(parts, r.body, fitmentId, new Date().toISOString());
      // store.save drops a record it could not read back; that is a failure here, not a trip to the tire list.
      if (!current.parseStored(JSON.stringify(record))) throw new Error('invalid');
      store.save(record);
      root.location.assign('/collections/all?vehicle=tires'); // the filtered list, even after an earlier Show all
    }).catch(function () {
      shopAgainStatus(btn, failText);
    }).then(function () {
      btn.disabled = false; // also after a successful hand-off: Back may restore this page from the cache
    });
  }

  doc.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('[data-vehicle-shop]');
    if (!btn || btn.disabled) return;
    shopAgain(btn);
  });

  /* ---------- picker ---------- */

  if (!picker) {
    renderChips();
    store.onChange(function (v) { var c = renderChips(); if (v) flash(c); });
    return api;
  }

  var selects = {};
  LEVELS.forEach(function (l) { selects[l] = picker.querySelector('[data-vehicle-level="' + l + '"]'); });
  var fitsEl = picker.querySelector('[data-vehicle-fits]');
  var msgEl = picker.querySelector('[data-vehicle-msg]');
  var retryEl = picker.querySelector('[data-vehicle-retry]');
  var saveEl = picker.querySelector('[data-vehicle-save]');
  var clearEl = picker.querySelector('[data-vehicle-clear]');
  var noteEl = picker.querySelector('[data-vehicle-note]');

  var anchor = null;
  var isOpen = false;
  var seq = 0; // a newer open / choice drops older answers
  var state = { body: null, fitmentId: null, retry: null, stored: null, changed: false };

  function selectedNow() {
    var o = { makeName: selectedText('make'), modelName: selectedText('model') };
    LEVELS.forEach(function (l) { o[l] = selects[l].value; });
    return o;
  }

  function refreshSave() {
    saveEl.disabled = !saveInput(selectedNow(), state.stored, state.fitmentId, state.changed);
  }

  function say(key, canRetry) {
    msgEl.textContent = key ? (i18n[key] || key) : '';
    msgEl.hidden = !key;
    retryEl.hidden = !(key && canRetry);
  }

  function selectedText(level) {
    var s = selects[level];
    return s && s.selectedIndex >= 0 ? s.options[s.selectedIndex].text : '';
  }

  function fillSelect(level, options, keep) {
    var s = selects[level];
    var label = s.getAttribute('data-placeholder') || '';
    s.innerHTML = '<option value="">' + esc(label) + '</option>' + (options || []).map(function (o) {
      return '<option value="' + esc(o.value) + '">' + esc(o.label) + '</option>';
    }).join('');
    s.disabled = !(options && options.length);
    if (keep) {
      s.value = keep;
      if (s.value !== keep) s.value = ''; // the stored value is not offered any more: show the placeholder
    }
  }

  function query(upto) {
    var parts = [];
    LEVELS.slice(0, LEVELS.indexOf(upto) + 1).forEach(function (l) {
      if (selects[l].value) parts.push(l + '=' + encodeURIComponent(selects[l].value));
    });
    return parts.join('&');
  }

  function resetBelow(level) {
    LEVELS.slice(LEVELS.indexOf(level) + 1).forEach(function (l) { fillSelect(l, []); });
    showFits(null);
  }

  function renderFits() {
    var fits = (state.body && state.body.fitments) || [];
    fitsEl.hidden = !fits.length;
    fitsEl.innerHTML = fits.map(function (f) {
      var c = current.fitmentChip(f);
      var on = f.id === state.fitmentId;
      return '<button type="button" class="toee-vehicle__fit' + (on ? ' is-on' : '') + '" data-id="' + esc(f.id) + '" aria-pressed="' + on + '">' +
        '<span class="toee-vehicle__fit-sizes">' + esc(c.sizes) + '</span>' +
        '<span class="toee-vehicle__fit-meta">' + esc(c.inches) + (c.tag ? ' ' + esc(c.tag) : '') + '</span></button>';
    }).join('');
    refreshSave();
    var us = state.body && state.body.vehicle && state.body.vehicle.market === 'US';
    noteEl.textContent = us ? (i18n.us_data || '') : '';
    noteEl.hidden = !us;
  }

  /** body = {vehicle, fitments} (a fitment answer, or the stored car), or null to empty the list. */
  function showFits(body, preferred) {
    state.body = body;
    state.fitmentId = body ? current.pickFitmentId(body.fitments, preferred) : null;
    renderFits();
  }

  /** Fill `level` from the server; `keep` re-selects a stored value. Resolves true when it worked. */
  function loadLevel(level, keep, mine, resume) {
    var prev = LEVELS[LEVELS.indexOf(level) - 1];
    var q = prev ? query(prev) : '';
    return getJson(BASE + '/vehicle/options' + (q ? '?' + q : '')).then(function (r) {
      if (mine !== seq) return false;
      if (!r.ok) throw new Error('options');
      fillSelect(level, r.body.options || [], keep);
      refreshSave();
      // Focus waits on the dialog until Year can take it (a disabled select cannot).
      if (level === 'year' && doc.activeElement === picker) selects.year.focus();
      return true;
    }).catch(function () {
      if (mine !== seq) return false;
      // While opening on the stored car, Retry resumes the whole chain, not just this level.
      state.retry = function () { picker.focus({ preventScroll: true }); say(''); if (resume) resume(); else loadLevel(level, keep, mine); };
      say('picker_error', true);
      return false;
    });
  }

  function loadFitment(mine, silent) {
    return getJson(BASE + '/vehicle/fitment?' + query('trim')).then(function (r) {
      if (mine !== seq) return;
      if (!r.ok) {
        if (r.body && r.body.error === 'no_fitment') { showFits(null); say('picker_no_fitment', false); return; }
        throw new Error('fitment');
      }
      showFits(r.body, state.fitmentId); // keeps the stored or chosen Fitment while the new list still has it
    }).catch(function () {
      if (mine !== seq || silent) return; // a refresh of the stored car may fail quietly: the stored sizes still work
      showFits(null);
      state.retry = function () { picker.focus({ preventScroll: true }); say(''); loadFitment(mine); };
      say('picker_error', true);
    });
  }

  function onSelect(level) {
    var i = LEVELS.indexOf(level);
    var mine = ++seq;
    state.changed = true;
    say('');
    resetBelow(level);
    if (!selects[level].value) return;
    if (i < LEVELS.length - 1) loadLevel(LEVELS[i + 1], '', mine);
    else loadFitment(mine);
  }

  /** Open with the stored car's four levels and Fitment pre-selected, then refresh its sizes. */
  function prefill(v, mine) {
    var step = prefillPlan(v, selectedNow());
    if (!step) { loadFitment(mine, true); return; }
    loadLevel(step.level, step.keep, mine, function () { prefill(v, mine); }).then(function (ok) {
      // A level that no longer offers the stored value stops here: the shopper picks from that level on.
      if (ok && mine === seq && selects[step.level].value === step.keep) prefill(v, mine);
    });
  }

  function place() {
    if (!anchor || !isOpen) return;
    var phone = root.matchMedia && root.matchMedia('(max-width: 600px)').matches;
    var a = anchor.getBoundingClientRect();
    var header = doc.getElementById('header');
    var top = a.bottom + 4;
    if (phone) {
      var h = header && header.getBoundingClientRect();
      top = h && h.bottom > 0 ? h.bottom : a.bottom;
      picker.style.left = '';
    } else {
      var w = picker.offsetWidth;
      picker.style.left = Math.max(8, Math.min(a.right - w, root.innerWidth - w - 8)) + 'px';
    }
    picker.style.top = Math.max(0, top) + 'px';
  }

  function close(returnFocus) {
    if (!isOpen) return;
    isOpen = false;
    seq++;
    picker.classList.remove('is-open');
    picker.hidden = true;
    var a = anchor;
    if (a && a.hasAttribute('aria-expanded')) a.setAttribute('aria-expanded', 'false');
    if (returnFocus && a) a.focus();
  }

  function openPicker(anchorEl) {
    var a = anchorEl || visibleChip();
    if (!a) return;
    if (isOpen) close(false);
    anchor = a;
    isOpen = true;
    var mine = ++seq;
    if (a.hasAttribute('aria-expanded')) a.setAttribute('aria-expanded', 'true');
    var v = store.read();
    state.stored = v;
    state.changed = false;
    LEVELS.forEach(function (l) { fillSelect(l, []); });
    say('');
    showFits(null);
    clearEl.hidden = !v;
    picker.hidden = false;
    place();
    void picker.offsetWidth; // let the 150ms fade start from the hidden state
    picker.classList.add('is-open');
    picker.focus({ preventScroll: true }); // the selects start disabled; Year takes focus once it has loaded
    if (v) {
      // Show the stored sizes at once; the server answer replaces them when it arrives.
      showFits({ vehicle: { label: v.label, market: v.market }, fitments: v.fitments }, v.fitmentId);
      prefill(v, mine);
    } else loadLevel('year', '', mine);
  }

  /* ---------- events ---------- */

  LEVELS.forEach(function (l) {
    selects[l].addEventListener('change', function () { onSelect(l); });
  });

  fitsEl.addEventListener('click', function (e) {
    var b = e.target.closest('[data-id]');
    if (!b || !fitsEl.contains(b)) return;
    state.fitmentId = b.getAttribute('data-id'); // only the selection changes; Save writes
    renderFits();
    var again = fitsEl.querySelector('[data-id="' + state.fitmentId + '"]');
    if (again) again.focus();
  });

  retryEl.addEventListener('click', function () { if (state.retry) state.retry(); });

  saveEl.addEventListener('click', function () {
    var q = saveInput(selectedNow(), state.stored, state.fitmentId, state.changed);
    if (!state.body || !q) return;
    store.save(current.fromFitmentResponse(q, state.body, state.fitmentId, new Date().toISOString()));
    close(true);
  });

  clearEl.addEventListener('click', function () {
    store.clear();
    close(true);
  });

  picker.querySelector('[data-vehicle-close]').addEventListener('click', function () { close(true); });

  // Escape only counts when focus is in the picker or on the chip, so other dialogs keep their own Escape.
  function onKey(e) {
    if (e.key === 'Escape' && isOpen) { e.preventDefault(); close(true); }
  }
  picker.addEventListener('keydown', onKey);
  doc.addEventListener('keydown', function (e) {
    if (e.target && e.target.closest && e.target.closest('[data-vehicle-chip]')) onKey(e);
  });

  doc.addEventListener('click', function (e) {
    var chip = e.target.closest && e.target.closest('[data-vehicle-chip]');
    if (chip) {
      if (isOpen && anchor === chip) close(true); else openPicker(chip);
      return;
    }
    if (!isOpen || !isOutsideClick(picker, anchor, e.target, doc)) return;
    var active = doc.activeElement;
    close(!active || active === doc.body || picker.contains(active));
  });

  root.addEventListener('resize', place);
  root.addEventListener('scroll', place, { passive: true });

  doc.body.appendChild(picker); // out of the header's list, so its layout rules and overflow can't touch it
  api.openPicker = openPicker;
  api.closePicker = function () { close(false); }; // no focus move: the caller is hiding the anchor
  renderChips();
  store.onChange(function (v) {
    var chips = renderChips();
    if (v) flash(chips);
  });
  return api;
});
